import { motion } from "framer-motion";
import { ArrowUpRight, Play, Apple, BellRing } from "lucide-react";
import { trackEvent } from "@/lib/api";

const reveal = {
  initial: { opacity: 0, y: 48 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Apps({ apps }) {
  const android = apps.android || {};
  const ios = apps.ios || {};
  const androidLive = android.enabled && android.link;
  const iosLive = ios.enabled && ios.link;

  return (
    <section id="apps" data-testid="apps-section" className="bg-charcoal py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p {...reveal} className="text-xs uppercase tracking-[0.35em] text-gold font-medium">
          The Apps
        </motion.p>
        <motion.h2
          {...reveal}
          className="mt-6 font-serif font-medium tracking-tight text-ivory text-4xl md:text-6xl max-w-3xl leading-[1.05]"
        >
          Carry the sacred, <span className="italic text-gold">wherever</span> you go.
        </motion.h2>

        <div className="mt-16 md:mt-20 grid md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            {...reveal}
            whileHover={{ y: -8 }}
            className="relative overflow-hidden bg-vermillion text-ivory p-10 md:p-14"
            data-testid="android-app-card"
          >
            <span className="absolute -right-4 -bottom-16 font-serif text-[13rem] leading-none text-ivory/10 select-none" aria-hidden="true">
              A
            </span>
            <div className="relative">
              <span className="inline-flex w-12 h-12 border border-ivory/40 items-center justify-center">
                <Play size={20} strokeWidth={1.5} />
              </span>
              <h3 className="mt-8 font-serif text-3xl md:text-4xl font-medium" data-testid="android-app-name">
                {android.name || "Android App"}
              </h3>
              <p className="mt-4 text-ivory/75 text-sm md:text-base max-w-sm leading-relaxed">
                Daily updates, temple details, artist profiles and stories — the complete Theyyam companion in your pocket.
              </p>
              {androidLive ? (
                <a
                  href={android.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("download_now", { button_name: android.button_text || "Download Now", destination_url: android.link })}
                  data-testid="android-download-button"
                  className="group mt-10 inline-flex items-center gap-3 bg-ivory text-vermillion text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 transition-colors duration-300 hover:bg-goldlight hover:text-charcoal"
                >
                  {android.button_text || "Download Now"}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              ) : (
                <button
                  disabled
                  data-testid="android-coming-soon-button"
                  className="mt-10 inline-flex items-center gap-3 bg-ivory/15 text-ivory/60 text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 cursor-not-allowed"
                >
                  <BellRing size={15} />
                  {android.button_text || "Coming Soon"}
                </button>
              )}
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.15 }}
            whileHover={{ y: -8 }}
            className="relative overflow-hidden border border-ivory/15 bg-ivory/[0.04] p-10 md:p-14"
            data-testid="ios-app-card"
          >
            <span className="absolute -right-4 -bottom-16 font-serif text-[13rem] leading-none text-ivory/5 select-none" aria-hidden="true">
              i
            </span>
            <div className="relative">
              <span className="inline-flex w-12 h-12 border border-ivory/25 text-ivory/60 items-center justify-center">
                <Apple size={20} strokeWidth={1.5} />
              </span>
              <h3 className="mt-8 font-serif text-3xl md:text-4xl font-medium text-ivory" data-testid="ios-app-name">
                {ios.name || "iOS App"}
              </h3>
              <p className="mt-4 text-ivory/50 text-sm md:text-base max-w-sm leading-relaxed">
                The iOS experience is being crafted with the same devotion. It will be worth the wait.
              </p>
              {iosLive ? (
                <a
                  href={ios.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="ios-download-button"
                  className="mt-10 inline-flex items-center gap-3 bg-ivory text-charcoal text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 transition-colors duration-300 hover:bg-goldlight"
                >
                  {ios.button_text || "Download"}
                  <ArrowUpRight size={16} />
                </a>
              ) : (
                <button
                  disabled
                  data-testid="ios-coming-soon-button"
                  className="mt-10 inline-flex items-center gap-3 bg-ivory/10 text-ivory/40 text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 cursor-not-allowed"
                >
                  <BellRing size={15} />
                  {ios.button_text || "Coming Soon"}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
